import qs from 'query-string';

import client, { createClient } from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';
import { conditionalObject } from 'utils/objects';

const paramsSerializer = params =>
  qs.stringify(params, {
    arrayFormat: 'comma',
    encode: false,
  });

/**
 * Used to fetch all experience types for both students and staff
 * @param studentId - The student's id (only used to fetch student exp types)
 * @param params.isActive {bool} - If true, show only isActive exp types, if
 * false, show only archived exp types, else show both active and archived
 */
export const getExperienceTypeList = ({
  version = 'v1',
  studentId = null,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/`,
      method: RequestMethod.GET,
      params: { student: studentId, ...params },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to reorder experience type list
 * @param experienceTypeId
 * @param payload.order {number} - The new order value which must be greater than 0
 */
export const reorderExperienceTypeList = ({
  version = 'v1',
  experienceTypeId,
  payload,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getExperienceTypeIndicators = ({
  version = 'v1',
  experienceTypeId,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/indicators/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getExperienceTypeViewableList = ({
  version = 'v1',
  student = undefined,
  isActive = undefined,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/viewable/`,
      method: RequestMethod.GET,
      params: { student, isActive, ...params },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getExperienceTypeRecordableList = ({
  version = 'v1',
  student = undefined,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/recordable/`,
      method: RequestMethod.GET,
      params: { student, ...params },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getExperienceType = ({
  experienceTypeId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createExperienceType = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateExperienceType = ({
  experienceTypeId,
  payload,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteExperienceType = ({
  experienceTypeId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getExperienceTrackingFieldList = ({
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/fields/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createExperienceTrackingField = ({
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/fields/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateExperienceTrackingField = ({
  fieldId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/fields/${fieldId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteExperienceTrackingField = ({
  fieldId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/fields/${fieldId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getExperienceTrackingFieldOptionList = ({
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/field-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createExperienceTrackingFieldOption = ({
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/field-options/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateExperienceTrackingFieldOption = ({
  fieldOptionId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/field-options/${fieldOptionId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteExperienceTrackingFieldOption = ({
  fieldOptionId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/field-options/${fieldOptionId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSessionHoursTracked = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/hours-tracked/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSessionCounts = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/counts/`,
      method: RequestMethod.GET,
      params,
      headers: { 'User-Timezone': undefined },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSessionListByEntry = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/`,
      method: RequestMethod.GET,
      params,
      headers: { 'User-Timezone': undefined },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Fetches a list of students that have sessions for the given experience type with the number of hours / entries for each status for each student.
 * @param experienceTypeId {number} - The experience type id
 * @param params.programId {number} - Optionally filter by programId
 */
export const getSessionListByStudent = ({
  experienceTypeId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/completion-metrics/`,
      method: RequestMethod.GET,
      params,
      headers: { 'User-Timezone': undefined },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Fetches an aggregate of the number of hours / entries for each status for the given experience type.
 * @param experienceTypeId {number} - The experience type id
 * @param params.programId {number} - Optionally filter by programId
 */
export const getSessionStatusCounts = ({
  experienceTypeId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/completion-metrics/aggregate/`,
      method: RequestMethod.GET,
      params,
      headers: { 'User-Timezone': undefined },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEmptyTimesheetPlacements = ({
  experienceTypeId,
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/completion-metrics/empty-timesheets/`,
      method: RequestMethod.GET,
      params,
      headers: { 'User-Timezone': undefined },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSessionUUIDList = ({
  params = {},
  index,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/uuid-list/${index}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrgSessionList = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/organizations/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSession = ({
  sessionUuid,
  experienceTypeId,
  viewingAsSupervisor,
  studentId,
  ipTimesheetParams,
  viewingAlum,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/${sessionUuid}/`,
      method: RequestMethod.GET,
      params: {
        viewingAsSupervisor,
        experienceTypes: experienceTypeId,
        studentId,
        ...ipTimesheetParams,
        ...conditionalObject(viewingAlum, {
          students: studentId,
        }),
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createSession = ({ payload, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateSession = ({
  sessionUuid,
  payload,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/${sessionUuid}/`,
      method: RequestMethod.PUT,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteSession = ({ sessionUuid, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/${sessionUuid}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const rejectSession = ({
  sessionUuid,
  payload,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/${sessionUuid}/reject/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const approveSession = ({
  sessionUuid,
  payload,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/${sessionUuid}/approve/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const undoSessionApproval = ({
  sessionUuid,
  payload,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/${sessionUuid}/undo/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getExperienceTrackingEmployers = ({
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/employers/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createExperienceTrackingEmployer = ({
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/employers/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getExperienceTrackingSupervisors = ({
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/supervisors/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createExperienceTrackingSupervisor = ({
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/supervisors/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createSessionFieldOption = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/session-field-options/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateSessionFieldOption = ({
  payload,
  sessionFieldOptionId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/session-field-options/${sessionFieldOptionId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteSessionFieldOption = ({
  sessionFieldOptionId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/session-field-options/${sessionFieldOptionId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createSessionFreeResponse = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/free-responses/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateSessionFreeResponse = ({
  freeResponseId,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/free-responses/${freeResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createSessionFile = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/session-files/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteSessionFile = ({ sessionFileId, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/session-files/${sessionFileId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Update experience question field order
 * @param id
 * @param payload - { order: NEW_INDEX_VALUE }, NEW_INDEX_VALUE must be greater
 * than 0
 */
export const updateExperienceTypeField = ({
  id,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-type-fields/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to archive an experience type for staff
 * @param id - The experience type id
 */
export const archiveExperienceType = ({
  id,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${id}/archive/`,
      method: RequestMethod.PATCH,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to restore an experience type for staff that was previously archived
 * @param id - The experience type id
 */
export const restoreExperienceType = ({
  id,
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${id}/restore/`,
      method: RequestMethod.PATCH,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by Ohio Means Jobs initiative or Ohio IRC
 * @param params.experienceTypes {number} - The experience type id
 */
export const getInitiativeSummary = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/initiative-summary/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used by Ohio Means Jobs initiative
 * @param params.experienceTypeIds {number} - The experience type id
 * @param params.studentId {number} - The student id
 */
export const downloadInitiativePdf = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/initiative-pdf/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Pins the experience type
 * @param experienceTypeId {number}
 */
export const pinExperienceType = ({ version = 'v1', experienceTypeId } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/pin/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Unpins the experience type
 * @param experienceTypeId {number}
 */
export const unpinExperienceType = ({
  version = 'v1',
  experienceTypeId,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/unpin/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to populate the activity feed drawer
 */
export const getActivityFeed = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/activity/`,
      method: RequestMethod.GET,
      paramsSerializer: { serialize: paramsSerializer },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Used to fetch the list of credentials
 */
export const getCredentialList = ({
  version = 'v1',
  experienceTypeId,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/credentials/`,
      method: RequestMethod.GET,
      paramsSerializer: { serialize: paramsSerializer },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPredefinedEntries = ({
  version = 'v1',
  params,
  experienceTypeId,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/predefined-entries/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createPredefinedEntry = ({
  version = 'v1',
  experienceTypeId,
  payload = {},
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/predefined-entries/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updatePredefinedEntry = ({
  version = 'v1',
  experienceTypeId,
  predefinedEntryId,
  payload = {},
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/predefined-entries/${predefinedEntryId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateCredentials = ({
  version = 'v1',
  experienceTypeId,
  predefinedEntryId,
  payload = {},
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/credentials/${predefinedEntryId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deletePredefinedEntry = ({
  version = 'v1',
  experienceTypeId,
  predefinedEntryId,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/predefined-entries/${predefinedEntryId}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPredefinedEntryCounts = ({
  version = 'v1',
  experienceTypeId,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/predefined-entries/count/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCredentialsCounts = ({
  version = 'v1',
  experienceTypeId,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/credentials/count/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const bulkDeletePredefinedEntry = ({
  version = 'v1',
  experienceTypeId,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/predefined-entries/bulk-delete/`,
      method: RequestMethod.DELETE,
      paramsSerializer: { serialize: paramsSerializer },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const bulkUpdatePredefinedEntry = ({
  version = 'v1',
  experienceTypeId,
  payload = {},
  params,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/predefined-entries/bulk-update/`,
      method: RequestMethod.PATCH,
      paramsSerializer: { serialize: paramsSerializer },
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const bulkUpdateCredentials = ({
  version = 'v1',
  experienceTypeId,
  payload = {},
  params,
} = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/experience-types/${experienceTypeId}/credentials/bulk-update/`,
      method: RequestMethod.PATCH,
      paramsSerializer: { serialize: paramsSerializer },
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getUnusedSessions = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/experience-tracking/sessions/unused-sessions/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

const unauthenticatedClient = createClient({ unauthorizedInterceptor: false });

export const getViewablePublicPortfolioCategories = ({
  version = 'v1',
  studentId,
} = {}) =>
  unauthenticatedClient
    .request({
      url: `${version}/experience-tracking/experience-types/viewable-public/`,
      params: {
        student: studentId,
        tracking_type: 'portfolio_items',
      },
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getViewablePublicPortfolioItems = ({
  version = 'v1',
  studentId,
  experienceId,
} = {}) =>
  unauthenticatedClient
    .request({
      url: `${version}/experience-tracking/sessions/public-portfolio/${studentId}/`,
      params: {
        experience_types: experienceId,
      },
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPublicSession = ({
  version = 'v1',
  studentId,
  sessionUuid,
  experienceTypeId,
} = {}) =>
  unauthenticatedClient
    .request({
      url: `${version}/experience-tracking/sessions/public-portfolio-session-details/${studentId}/${sessionUuid}/`,
      params: {
        experience_types: experienceTypeId,
      },
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * get list of credential experience types for a given initiative
 * @param {Object} params - query params
 * @param {Number} params.initiative - initiative name (eg. "texas_industry_based_certification")
 * @returns {Promise} - list of credential experience types
 */
export const listCredentials = ({
  version = 'v1',
  params = {},
  payload = {},
}) =>
  client
    .request({
      url: `${version}/experience-tracking/credentials/`,
      method: RequestMethod.GET,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);
